import * as React from 'react';
import { useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { PostScheduleWeekController, PostScheduleWeekControllerProps } from './PostScheduleWeek.controller';
import { PostScheduleViewProps } from '../PostSchedule.view';
import { ApolloError } from '@apollo/client';
import {
  GetPostScheduleWeekQuery,
  GetPostScheduleWeekQueryVariables,
  QueueContext,
  QueueFilterInput,
  useGetPostQueueCountLazyQuery,
  useGetPostScheduleWeekLazyQuery
} from '../../../../models/graphql';
import { clearPostCache, defaultQueueInput } from '../PostSchedule.apollo';
import { remapApolloErrors } from '../../../../helpers/remapApolloErrors';
import { AppContext } from '../../../../providers/AppProvider';
import { showToast } from '../../../../helpers/showToast';
import { QueueCountContext } from '../../../../providers/QueueCountProvider';
import { PostScheduleContext } from '../../../../providers/PostScheduleProvider';
import useFetchQueueWithPolling, { UseQueryPollingProps } from '../../../../hooks/useFetchQueueWithPolling';
import { isEqual } from 'lodash';
import { format, isAfter } from 'date-fns';
import { DateTimeStringFormat } from '../../../../constants/time-formats';
import { TopNavContext } from '../../../../providers/TopNavProvider';
import { useDidMount } from '../../../../hooks/useDidMount';
import { QueueFiltersType } from '../../../../models/post';
import { TDateTimeString } from '../../../../models/date';
import { formatInTimeZone, zonedTimeToUtc } from 'date-fns-tz';

export type PostScheduleWeekApolloProps = PostScheduleViewProps & {
  // ...
};

// Convert queueFilters to QueueFilterInput by omitting fetchPosts
const mapQueueFiltersToQueueFilterInput = (queueFilters: QueueFiltersType): QueueFilterInput => {
  const { fetchPosts: _, ...queueFiltersInput } = queueFilters;
  return queueFiltersInput;
};

export const PostScheduleWeekApollo = (props: PostScheduleWeekApolloProps): JSX.Element => {
  const { selectedWorkspaceId } = useContext(AppContext);
  const { dispatch } = useContext(TopNavContext);
  const { setQueueCounts } = useContext(QueueCountContext);
  const { startDate, endDate, queueFilters, wsTz } = props;
  const {
    reFetch,
    setRefetch,
    setLoadingInitial,
    triggerReShuffleFetch,
    setTriggerReShuffleFetch,
    triggerDeleteReFetch,
    setTriggerDeleteReFetch
  } = useContext(PostScheduleContext);
  const { startQuery } = useFetchQueueWithPolling();
  const [getPostScheduleWeek, postScheduleWeekResults] = useGetPostScheduleWeekLazyQuery({
    fetchPolicy: 'network-only'
  });
  const [getPostQueueCount] = useGetPostQueueCountLazyQuery({
    fetchPolicy: 'network-only'
  });

  const queueFiltersInput = useMemo(
    (): QueueFilterInput => mapQueueFiltersToQueueFilterInput(queueFilters),
    [queueFilters]
  );

  const queueFiltersInputRef = useRef(queueFiltersInput);
  const dateValuesRef = useRef({ startDate, endDate });

  const dateNow = new Date();
  const realStart = isAfter(dateNow, startDate) ? dateNow : startDate;
  const startUtcDate = formatInTimeZone(realStart, 'UTC', DateTimeStringFormat) as TDateTimeString;
  // const browserTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const formattedEndDateString = format(endDate, DateTimeStringFormat);
  const zonedEndDate = zonedTimeToUtc(formattedEndDateString, wsTz);
  const endUtcDate = formatInTimeZone(zonedEndDate, 'UTC', DateTimeStringFormat) as TDateTimeString;
  // const actualFormattedStartDate = isBefore(zonedStartDate)

  const getPostScheduleQueueCounts = useCallback(() => {
    getPostQueueCount({
      variables: {
        workspaceId: selectedWorkspaceId as string,
        queueFilters: queueFiltersInput,
        queueContext: QueueContext.SCHEDULE
      }
    })
      .then(results => {
        if (results.data?.workspace.queueCounts) {
          setQueueCounts(results.data.workspace.queueCounts);
        }
      })
      .catch((error: ApolloError) => {
        showToast({
          severity: 'error',
          summary: 'Retrieving Queue Counts',
          detail: remapApolloErrors(error)
        });
      });
  }, [getPostQueueCount, queueFiltersInput, selectedWorkspaceId, setQueueCounts]);

  const getPostScheduleWeekPostItems = useCallback(async () => {
    setLoadingInitial(true);

    clearPostCache(selectedWorkspaceId);

    const options = {
      workspaceId: selectedWorkspaceId,
      input: {
        ...defaultQueueInput,
        filters: {
          ...queueFiltersInput
        },
        ...{
          pagination: {
            dateRangePagination: {
              startDate: startUtcDate,
              endDate: endUtcDate
            }
          },
          context: QueueContext.SCHEDULE
        }
      }
    };

    const UseQueryPollingProps: UseQueryPollingProps<GetPostScheduleWeekQuery, GetPostScheduleWeekQueryVariables> = {
      queryFn: getPostScheduleWeek,
      options
    };

    await startQuery(UseQueryPollingProps).finally(() => {
      setLoadingInitial(false);
    });
  }, [
    endUtcDate,
    getPostScheduleWeek,
    queueFiltersInput,
    selectedWorkspaceId,
    setLoadingInitial,
    startQuery,
    startUtcDate
  ]);

  const reFetchPosts = useCallback(() => {
    const errorMsg = 'Error trying to update your view';
    getPostScheduleWeekPostItems()
      .then(() => {
        getPostScheduleQueueCounts();
      })
      .catch((error: ApolloError) => {
        showToast({
          severity: 'error',
          summary: errorMsg,
          detail: remapApolloErrors(error)
        });
      });
    return undefined;
  }, [getPostScheduleQueueCounts, getPostScheduleWeekPostItems]);

  const fetchInitialPostItems = useCallback(() => {
    Promise.all([getPostScheduleWeekPostItems(), getPostScheduleQueueCounts()]).finally();
  }, [getPostScheduleQueueCounts, getPostScheduleWeekPostItems]);

  useEffect(() => {
    if (reFetch) {
      setLoadingInitial(true);

      clearPostCache(selectedWorkspaceId);

      Promise.all([reFetchPosts()]).finally(() => {
        setRefetch(false);
      });
    }
  }, [reFetch, reFetchPosts, selectedWorkspaceId, setLoadingInitial, setRefetch]);

  useEffect(() => {
    if (
      !isEqual(queueFiltersInputRef.current, queueFiltersInput) ||
      dateValuesRef.current.startDate !== startDate ||
      dateValuesRef.current.endDate !== endDate
    ) {
      fetchInitialPostItems();
      queueFiltersInputRef.current = queueFiltersInput;
      dateValuesRef.current = {
        startDate,
        endDate
      };
    }
  }, [endDate, fetchInitialPostItems, getPostScheduleQueueCounts, queueFiltersInput, startDate]);

  useEffect(() => {
    if (triggerReShuffleFetch) {
      fetchInitialPostItems();
      setTriggerReShuffleFetch(false);
    }
  }, [fetchInitialPostItems, setTriggerReShuffleFetch, triggerReShuffleFetch]);

  useEffect(() => {
    if (triggerDeleteReFetch) {
      fetchInitialPostItems();
      setTriggerDeleteReFetch(false);
    }
  }, [fetchInitialPostItems, setTriggerDeleteReFetch, triggerDeleteReFetch]);

  useDidMount(() => {
    fetchInitialPostItems();

    dispatch({
      type: 'setNewPostCloseCallback',
      payload: status => {
        if (status) {
          fetchInitialPostItems();
        }
      }
    });

    return () => {
      dispatch({
        type: 'setNewPostCloseCallback',
        payload: undefined
      });
    };
  });

  const scheduleItems = postScheduleWeekResults.data?.workspace.queue.dateRangePagination?.items || [];

  const controllerProps: PostScheduleWeekControllerProps = {
    ...props,
    ...{
      wsTz,
      scheduleItems,
      reFetchPosts,
      getPostScheduleWeekPostItems
    }
  };

  return <PostScheduleWeekController {...controllerProps} />;
};
